import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CFormLabel,
  CFormTextarea,
} from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperclip, faXmark } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { toast } from "react-toastify";

import WeAllTeamTabs from "@/components/weAllTeam/WeAllTeamTabs";
import WeAllTeamAvatar from "@/components/weAllTeam/WeAllTeamAvatar";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  MESSAGE_MAX_LENGTH,
  toBool,
} from "@/components/weAllTeam/weAllTeamUtils";
import WeAllTeamService from "@/api/weAllTeamService";
import { useTranslationContext } from "@/context/TranslationContext";
import { weAllTeam as tr } from "@/constants/translation/weAllTeam.translation";
import { weAllTeam as routes } from "@/libs/routes";
import { useSendWeAllTeamMessage } from "@/hooks/useWeAllTeam";

const toOption = (user) => ({ value: user.uuid, label: user.full_name, user });

// Picker colours follow the panel's theme variables so it stays readable in dark mode
const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 44,
    backgroundColor: "var(--cui-body-bg)",
    borderColor: "var(--cui-border-color)",
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "var(--cui-body-bg)",
    zIndex: 10,
  }),
  option: (base, state) => ({
    ...base,
    color: "var(--cui-body-color)",
    backgroundColor: state.isFocused ? "var(--cui-tertiary-bg)" : "transparent",
    cursor: "pointer",
  }),
  singleValue: (base) => ({ ...base, color: "var(--cui-body-color)" }),
  input: (base) => ({ ...base, color: "var(--cui-body-color)" }),
  placeholder: (base) => ({ ...base, color: "var(--cui-secondary-color)" }),
};

/**
 * New message: search and pick a user, write the message and send it as the WeAll Team. On success the new
 * conversation opens.
 */
const WeAllTeamNewMessage = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const fileInput = useRef(null);
  const [errors, setErrors] = useState({});
  const searchTimeout = useRef(null);

  // Server-side search with infinite scroll: typing restarts at page 1, scrolling to the end of the list loads the
  // next page. A request counter drops answers that arrive after a newer search was started.
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  const fetchUsers = useCallback(async (search, pageNumber) => {
    const current = ++requestId.current;
    setLoading(true);
    try {
      const response = await WeAllTeamService.searchUsers(search, pageNumber);
      if (current !== requestId.current) return;
      const users = (response?.data?.users || []).map(toOption);
      setOptions((previous) => {
        if (pageNumber === 1) return users;
        const known = new Set(previous.map((option) => option.value));
        return [
          ...previous,
          ...users.filter((option) => !known.has(option.value)),
        ];
      });
      setPage(pageNumber);
      setHasMore(toBool(response?.data?.pagination?.has_more));
    } catch {
      if (current === requestId.current && pageNumber === 1) setOptions([]);
      if (current === requestId.current) setHasMore(false);
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, []);

  // First page on mount and whenever the typed text settles
  useEffect(() => {
    const timer = setTimeout(() => fetchUsers(inputValue.trim(), 1), 300);
    return () => clearTimeout(timer);
  }, [inputValue, fetchUsers]);

  const loadMore = () => {
    if (hasMore && !loading) fetchUsers(inputValue.trim(), page + 1);
  };

  const { mutate: send, isPending } = useSendWeAllTeamMessage(
    (response) => {
      toast.success(response?.message || t(tr.toast_sent));
      const conversationId = response?.data?.conversation_id;
      navigate(
        conversationId
          ? routes.conversation.replace(":id", conversationId)
          : routes.inbox,
      );
    },
    (error) =>
      toast.error(error?.response?.data?.message || t(tr.toast_failed)),
  );

  const handleSend = () => {
    const nextErrors = {};
    if (!selected) nextErrors.user = t(tr.validation_user);
    if (!message.trim() && !file)
      nextErrors.message = t(tr.validation_message_or_file);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    send({ user_uuid: selected.value, message: message.trim(), file });
  };

  return (
    <div className="weall-team-new">
      <WeAllTeamTabs />
      <CCard>
        <CCardBody>
          <div className="mb-4" style={{ maxWidth: 640 }}>
            <CFormLabel>{t(tr.select_user_label)}</CFormLabel>
            <Select
              options={options}
              isLoading={loading}
              filterOption={null}
              inputValue={inputValue}
              onInputChange={(value, { action }) => {
                if (action === "input-change") setInputValue(value);
                // react-select hides the chosen value while text is typed, so clear it once a user is picked
                if (action === "set-value" || action === "menu-close")
                  setInputValue("");
              }}
              onMenuScrollToBottom={loadMore}
              value={selected}
              onChange={(option) => {
                setSelected(option);
                setErrors((prev) => ({ ...prev, user: undefined }));
              }}
              placeholder={t(tr.select_user_placeholder)}
              noOptionsMessage={() => t(tr.select_user_no_options)}
              loadingMessage={() => t(tr.select_user_loading)}
              styles={selectStyles}
              classNamePrefix="weall-team-select"
              formatOptionLabel={({ user }) => (
                <div className="d-flex align-items-center gap-2">
                  <WeAllTeamAvatar src={user.profile_photo_url} size={28} />
                  <div>
                    <div>{user.full_name}</div>
                    <small className="text-muted">
                      {[user.user_name && `@${user.user_name}`, user.email]
                        .filter(Boolean)
                        .join(" · ")}
                    </small>
                  </div>
                </div>
              )}
            />
            {errors.user ? (
              <div className="text-danger small mt-1">{errors.user}</div>
            ) : (
              <div className="text-muted small mt-1">
                {t(tr.select_user_hint)}
              </div>
            )}
          </div>

          <div className="mb-3" style={{ maxWidth: 640 }}>
            <CFormLabel>{t(tr.message_label)}</CFormLabel>
            <CFormTextarea
              rows={6}
              value={message}
              maxLength={MESSAGE_MAX_LENGTH}
              placeholder={t(tr.message_placeholder)}
              onChange={(e) => {
                setMessage(e.target.value);
                setErrors((prev) => ({ ...prev, message: undefined }));
              }}
            />
            <div className="d-flex justify-content-between mt-1">
              <div className="text-danger small">{errors.message}</div>
              <small className="text-muted">
                {message.length}/{MESSAGE_MAX_LENGTH}
              </small>
            </div>
          </div>

          <div className="mb-3" style={{ maxWidth: 640 }}>
            <input
              ref={fileInput}
              type="file"
              hidden
              accept={ATTACHMENT_ACCEPT}
              onChange={(e) => {
                const picked = e.target.files?.[0];
                if (!picked) return;
                if (picked.size > ATTACHMENT_MAX_BYTES) {
                  toast.error(t(tr.file_too_large));
                  e.target.value = "";
                  return;
                }
                setFile(picked);
                setErrors((prev) => ({ ...prev, message: undefined }));
              }}
            />
            <CButton
              color="secondary"
              variant="outline"
              size="sm"
              onClick={() => fileInput.current?.click()}
            >
              <FontAwesomeIcon icon={faPaperclip} className="me-2" />
              {t(tr.attach_file)}
            </CButton>
            {file && (
              <span className="ms-3">
                {file.name}{" "}
                <button
                  type="button"
                  title={t(tr.remove_file)}
                  className="border-0 bg-transparent"
                  onClick={() => {
                    setFile(null);
                    if (fileInput.current) fileInput.current.value = "";
                  }}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </span>
            )}
          </div>

          <p className="text-muted small" style={{ maxWidth: 640 }}>
            {t(tr.sent_info)}
          </p>

          <CButton color="primary" disabled={isPending} onClick={handleSend}>
            {isPending ? t(tr.sending) : t(tr.send)}
          </CButton>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default WeAllTeamNewMessage;
